import { Fragment, Suspense, useEffect, useMemo, useRef, useState, type ComponentType, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, errorText, uploadFile, type UploadedFile } from "../api";
import { can, useAccess } from "../access";
import { useBusiness, useBusinesses } from "../business";
import { ConfirmDialog, Dialog } from "../dialog";
import { Banner, toast, useEditor } from "../editor";
import { CheckboxField, Field, SelectField, TextField } from "../fields";
import { Avatar, BusinessMark, go, useSession } from "../session";
import { SOLUTION_SETTINGS } from "../solutions/settings";
import { PaymentsSettings } from "./payments";
import { Badge, Card, Empty, Icon, Menu, Thumb, fmtBytes, plural } from "../ui";

/**
 * The inside of the Settings sheet (the sheet itself is in shell.tsx): the business, a search and every section on
 * the left, the section on the right under its own title. On a phone the list is a screen of its own (/settings)
 * and a section has "Settings" to go back to it.
 */

type SettingsLink = { key: string; label: string; icon: ComponentType; need?: string; Component: ComponentType };

const CORE: SettingsLink[] = [
  { key: "general", label: "General", icon: Icon.store, Component: GeneralSettings },
  { key: "solutions", label: "Solutions", icon: Icon.grid, Component: SolutionsSettings },
  { key: "team", label: "Team", icon: Icon.people, Component: TeamSettings },
  { key: "roles", label: "Roles", icon: Icon.shield, Component: RolesSettings },
  { key: "connectors", label: "Connectors", icon: Icon.plug, need: "business.connectors", Component: ConnectorsSettings },
  { key: "payments", label: "Payments", icon: Icon.card, need: "business.payments", Component: PaymentsSettings },
  { key: "files", label: "Files", icon: Icon.folder, Component: FilesSettings },
  { key: "timeline", label: "Timeline", icon: Icon.clock, need: "business.timeline", Component: TimelineSettings },
];

const PHONE = "(max-width: 760px)";
function usePhone(): boolean {
  const [phone, setPhone] = useState(() => !!window.matchMedia?.(PHONE).matches);
  useEffect(() => {
    const m = window.matchMedia?.(PHONE);
    if (!m) return;
    const on = () => setPhone(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return phone;
}

export function SettingsPage() {
  const { me } = useSession();
  const { data, has } = useBusiness();
  const { can: allowed } = useAccess();
  const phone = usePhone();
  const [find, setFind] = useState("");
  const path = useLocation().pathname;
  const section = path.split("/")[2] || "";
  const solutionLinks: SettingsLink[] = SOLUTION_SETTINGS.filter((s) => has(s.solution));
  const all = [...CORE, ...solutionLinks];
  const mine = (s: SettingsLink) => !s.need || allowed(s.need);
  if (!section && !phone) return <Navigate to="/settings/general" replace />;
  const q = find.trim().toLowerCase();
  const shows = (s: SettingsLink) => mine(s) && (!q || s.label.toLowerCase().includes(q));
  const core = CORE.filter(shows);
  const sols = solutionLinks.filter(shows);
  const here = all.find((s) => s.key === section);
  const item = (s: SettingsLink) => (
    <NavLink key={s.key} to={`/settings/${s.key}`} className={({ isActive }) => (isActive ? "is-active" : "")}>
      <s.icon />
      <span>{s.label}</span>
      <Icon.chevR />
    </NavLink>
  );
  const host = `${window.location.host}/${data.business.handle}`;
  return (
    <div className={`settings ${section ? "settings--section" : "settings--list"}`}>
      {!section && <h1 className="settings__phone-title">Settings</h1>}
      <aside className="settings__side">
        <div className="settings__club">
          <BusinessMark name={data.business.name} />
          <span>
            <strong>{data.business.name}</strong>
            <small>{host}</small>
          </span>
        </div>
        <label className="settings__find">
          <Icon.search />
          <input type="search" placeholder="Search" aria-label="Search settings" value={find} onChange={(e) => setFind(e.target.value)} />
        </label>
        <nav className="settings__nav" aria-label="Settings">
          {core.map(item)}
          {sols.length > 0 && <div className="settings__head">Solutions</div>}
          {sols.map(item)}
          {!core.length && !sols.length && <p className="settings__none">No settings match “{find.trim()}”.</p>}
        </nav>
        {me && (
          <Link to="/account" className="settings__me" title="Your login">
            <Avatar name={me.account.name} />
            <span>
              <strong>{me.account.name}</strong>
              <small>{me.account.email}</small>
            </span>
          </Link>
        )}
      </aside>
      {section && (
        <div className="settings__main">
          <Link to="/settings" className="settings__back">
            <Icon.chevL />
            Settings
          </Link>
          {!here ? (
            <Empty title="There's no setting here" text="It may belong to a solution that isn't switched on." />
          ) : !mine(here) ? (
            <Empty title="You don't have access to this setting" text="Ask the owner or an administrator to add it to your role in Settings › Roles." />
          ) : section === "roles" ? (
            <Suspense fallback={<div className="loading">Loading…</div>}><here.Component /></Suspense>
          ) : (
            <>
              <h1 className="settings__title">
                <here.icon />
                {here.label}
              </h1>
              <Suspense fallback={<div className="loading">Loading…</div>}><here.Component key={section} /></Suspense>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function useBizDates() {
  const { data } = useBusiness();
  return useMemo(() => {
    const f = new Intl.DateTimeFormat(data.business.locale || "en-AU", { dateStyle: "medium", timeStyle: "short", timeZone: data.business.timeZone || undefined });
    const d = new Intl.DateTimeFormat(data.business.locale || "en-AU", { dateStyle: "medium", timeZone: data.business.timeZone || undefined });
    return { when: (iso: string) => f.format(new Date(iso)), day: (iso: string) => d.format(new Date(iso)) };
  }, [data.business.locale, data.business.timeZone]);
}

/* ---------- General ---------- */

const LEARNING = [
  { value: "full", label: "Full", help: "Share anonymous patterns and get every improvement." },
  { value: "limited", label: "Limited", help: "Share counts only, never content." },
  { value: "off", label: "Off", help: "Share nothing. You still get improvements Mivio ships." },
] as const;

function GeneralSettings() {
  const { data, reload } = useBusiness();
  const qc = useQueryClient();
  const editable = can(data.me.permissions, "business.settings");
  const b = data.business;
  const zones = useMemo(() => Intl.supportedValuesOf("timeZone").map((z) => ({ value: z, label: z.replace(/_/g, " ") })), []);
  const currencies = useMemo(() => Intl.supportedValuesOf("currency").map((c) => ({ value: c, label: c })), []);
  const ed = useEditor({
    initial: { name: b.name, currency: b.currency, timeZone: b.timeZone, learning: b.learning },
    revision: 0,
    savedText: "Settings saved",
    save: async (d) => {
      await api("", { method: "PATCH", json: { ...d, name: d.name.trim() } });
      void qc.invalidateQueries({ queryKey: ["businesses"] });
      reload();
      return { document: { ...d, name: d.name.trim() }, revision: 0 };
    },
  });
  const d = ed.draft;
  return (
    <>
      {editable && ed.leaveGuard}
      {ed.notice && <Banner tone={ed.notice.tone} title={ed.notice.title}>{ed.notice.text}</Banner>}
      {!editable && <Banner tone="info" title="Only people who can change settings edit these">You can see them; ask the owner or an administrator to change them.</Banner>}
      <Card title="Business details">
        <fieldset className="fieldset-plain" disabled={!editable}>
          <TextField label="Name" value={d.name} maxLength={120} onChange={(v) => ed.set("name", v)} error={ed.errors.name} help="" />
          <Field label="Address" help="Where your team opens the business in Mivio. It can't be changed yet.">
            {(id, desc) => <input id={id} aria-describedby={desc} value={`${window.location.host}/${b.handle}`} readOnly />}
          </Field>
          <div className="grid-fields">
            <SelectField label="Currency" value={d.currency} options={currencies} onChange={(v) => ed.set("currency", v)} error={ed.errors.currency} />
            <SelectField label="Time zone" value={d.timeZone} options={zones} onChange={(v) => ed.set("timeZone", v)} error={ed.errors.timeZone} />
          </div>
        </fieldset>
      </Card>
      {data.siteUrl && (
        <Card title="Members' site" foot="Each solution adds its own pages to it when it's switched on.">
          <p className="acct-plain">
            <a className="link" href={data.siteUrl} target="_blank" rel="noopener">
              {data.siteUrl.replace(/^https?:\/\//, "")}
            </a>
          </p>
        </Card>
      )}
      <Card title="Learning across Mivio" foot="Personal details are removed inside your business before anything is shared.">
        <p className="acct-plain">What works here can help every business on Mivio, and theirs helps you.</p>
        <div className="rolepick">
          {LEARNING.map((o) => (
            <label key={o.value} className={`rolepick__row${d.learning === o.value ? " is-on" : ""}${editable ? "" : " is-disabled"}`}>
              <input type="radio" name="learning" checked={d.learning === o.value} disabled={!editable} onChange={() => ed.set("learning", o.value)} />
              <span className="rolepick__text">
                <strong>{o.label}</strong>
                <small>{o.help}</small>
              </span>
            </label>
          ))}
        </div>
      </Card>
    </>
  );
}

/* ---------- Solutions ---------- */

interface SolutionRow { key: string; name: string; outcome: string; version: string; on: boolean; dependsOn: string[]; switchedOnAt: string | null }

function SolutionsSettings() {
  const { data, reload } = useBusiness();
  const qc = useQueryClient();
  const editable = can(data.me.permissions, "business.settings");
  const q = useQuery({ queryKey: ["solutions"], queryFn: () => api<{ solutions: SolutionRow[] }>("/solutions").then((r) => r.solutions) });
  const [busy, setBusy] = useState<string | null>(null);
  const [off, setOff] = useState<SolutionRow | null>(null);
  const flip = async (s: SolutionRow, on: boolean) => {
    setBusy(s.key);
    try {
      const r = await api<{ solutions: SolutionRow[] }>(`/solutions/${s.key}/${on ? "on" : "off"}`, { method: "POST" });
      qc.setQueryData(["solutions"], r.solutions);
      toast(on ? `${s.name} switched on` : `${s.name} switched off`);
      setOff(null);
      reload();
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <Card flush foot={editable ? "Switching one off hides it for everyone and keeps its data. Switch it back on and everything is where you left it." : "Only people who can change settings switch solutions on and off."}>
        {q.isPending ? (
          <div className="loading">Loading…</div>
        ) : (
          <ul className="rows">
            {(q.data ?? []).map((s) => (
              <li key={s.key}>
                <span className="rows__text">
                  <strong>
                    {s.name}
                    {s.on ? <Badge tone="success">On</Badge> : <Badge tone="neutral">Off</Badge>}
                  </strong>
                  <small>{s.outcome}</small>
                </span>
                {editable && (
                  <span className="rows__tools">
                    {s.on ? (
                      <button type="button" className="btn btn--sm" disabled={busy === s.key} onClick={() => setOff(s)}>Switch off</button>
                    ) : (
                      <button type="button" className="btn btn--sm btn--primary" disabled={busy === s.key} onClick={() => void flip(s, true)}>
                        {busy === s.key ? "Switching on…" : "Switch on"}
                      </button>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <ConfirmDialog
        open={!!off}
        title={`Switch off ${off?.name ?? ""}?`}
        text={`Its pages disappear for everyone in ${data.business.name}. Nothing is deleted.`}
        confirmLabel="Switch off"
        tone="danger"
        busy={!!off && busy === off.key}
        onConfirm={() => off && void flip(off, false)}
        onCancel={() => setOff(null)}
      />
    </>
  );
}

/* ---------- Team ---------- */

interface Role { id: string; key: string; name: string; description: string; permissions: string[]; preset: boolean; members?: number }
interface CatalogGroup { group: string; permissions: { key: string; label: string }[] }
interface Member { id: string; accountId: string; name: string; email: string; role: { id: string; key: string; name: string }; since: string; you: boolean }
interface Invite { id: string; email: string; role: { id: string; name: string }; sentAt: string; expiresAt: string }

function useRoles() {
  return useQuery({ queryKey: ["roles"], queryFn: () => api<{ roles: Role[]; catalog: CatalogGroup[] }>("/roles") });
}

function TeamSettings() {
  const { data } = useBusiness();
  const qc = useQueryClient();
  const { day } = useBizDates();
  const manage = can(data.me.permissions, "business.members");
  const members = useQuery({ queryKey: ["members"], queryFn: () => api<{ members: Member[] }>("/members").then((r) => r.members) });
  const invites = useQuery({ queryKey: ["invites"], queryFn: () => api<{ invites: Invite[] }>("/invites").then((r) => r.invites), enabled: manage });
  const roles = useRoles();
  const assignable = (roles.data?.roles ?? []).filter((r) => r.key !== "owner");
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [handing, setHanding] = useState<Member | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = () => {
    void qc.invalidateQueries({ queryKey: ["members"] });
    void qc.invalidateQueries({ queryKey: ["invites"] });
    void qc.invalidateQueries({ queryKey: ["roles"] });
  };
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      toast(done);
      reload();
      return true;
    } catch (err) {
      toast(errorText(err), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const list = members.data ?? [];
  return (
    <>
      <div className="settings__headrow settings__headrow--flush">
        <p className="muted">{plural(list.length, "person", "people")} can open {data.business.name}.</p>
        {manage && (
          <div className="settings__actions">
            <button type="button" className="btn btn--primary" onClick={() => setInviting(true)}>Invite people</button>
          </div>
        )}
      </div>
      <Card flush foot="Everyone logs in with their own Mivio login. The owner and people who can manage the team invite others and change roles.">
        {members.isPending ? (
          <div className="loading">Loading…</div>
        ) : (
          <table className="table users-table">
            <thead>
              <tr>
                <th>Person</th>
                <th className="hide-sm">Since</th>
                <th>Role</th>
                {manage && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="cell">
                      <Avatar name={m.name || m.email} className="user-avatar" />
                      <div className="cell__text">
                        <strong>
                          {m.name || m.email} {m.you && <Badge tone="info">You</Badge>}
                        </strong>
                        <span>{m.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="hide-sm">{day(m.since)}</td>
                  <td>
                    {manage && m.role.key !== "owner" && !m.you ? (
                      <select className="select" aria-label={`Role for ${m.email}`} value={m.role.id} disabled={busy} onChange={(e) => void run(() => api(`/members/${m.id}`, { method: "PATCH", json: { roleId: e.target.value } }), "Role changed")}>
                        {assignable.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    ) : m.role.key === "owner" ? (
                      <strong className="owner-label">Owner</strong>
                    ) : (
                      m.role.name
                    )}
                  </td>
                  {manage && (
                    <td className="num cell--menu">
                      {!m.you && m.role.key !== "owner" && (
                        <Menu
                          compact
                          label="Actions"
                          icon={<Icon.more />}
                          ariaLabel={`Actions for ${m.name || m.email}`}
                          items={[
                            ...(data.me.owner ? [{ key: "owner", label: "Make owner", onSelect: () => { setConfirmText(""); setHanding(m); } }] : []),
                            { key: "remove", label: "Remove from the team", danger: true, onSelect: () => setRemoving(m) },
                          ]}
                        />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {manage && (invites.data ?? []).length > 0 && (
        <Card title="Waiting to join" flush>
          <ul className="rows">
            {invites.data!.map((i) => (
              <li key={i.id}>
                <span className="rows__text">
                  <strong>{i.email}</strong>
                  <small>{i.role.name} · the invite works until {day(i.expiresAt)}</small>
                </span>
                <span className="rows__tools">
                  <button type="button" className="btn btn--sm btn--plain" disabled={busy} onClick={() => void run(() => api(`/invites/${i.id}`, { method: "DELETE" }), "Invite cancelled")}>Cancel invite</button>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {inviting && <InviteDialog roles={assignable} onClose={() => setInviting(false)} onSent={reload} />}
      <ConfirmDialog
        open={!!removing}
        title={`Remove ${removing?.name || removing?.email || ""}?`}
        text={`They can't open ${data.business.name} any more. Their Mivio login stays, and so does everything they did here.`}
        confirmLabel="Remove"
        tone="danger"
        busy={busy}
        onConfirm={() => removing && void run(() => api(`/members/${removing.id}`, { method: "DELETE" }), "Removed from the team").then(() => setRemoving(null))}
        onCancel={() => setRemoving(null)}
      />
      <ConfirmDialog
        open={!!handing}
        title={`Hand ${data.business.name} to ${handing?.name || handing?.email || ""}?`}
        text="They become the owner and you become an Administrator. Only the new owner can hand it back."
        confirmLabel="Make owner"
        tone="danger"
        busy={busy || confirmText !== data.business.handle}
        onConfirm={() => handing && void run(() => api("/transfer-ownership", { method: "POST", json: { membershipId: handing.id, confirm: confirmText } }), "Ownership handed over").then((ok) => ok && go(`/${data.business.handle}`))}
        onCancel={() => setHanding(null)}
      >
        <TextField label={`Type ${data.business.handle} to confirm`} value={confirmText} onChange={setConfirmText} />
      </ConfirmDialog>
    </>
  );
}

function InviteDialog({ roles, onClose, onSent }: { roles: Role[]; onClose: () => void; onSent: () => void }) {
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(() => roles.find((r) => r.key === "staff")?.id ?? roles[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/invites", { method: "POST", json: { email: email.trim(), roleId } });
      toast(`Invite sent to ${email.trim()}`);
      onSent();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <Dialog title="Invite people" onClose={onClose} busy={busy}>
      <form onSubmit={(e) => void submit(e)}>
        <p>They get an email with a link to join. If they don't have a Mivio login yet, they make one first.</p>
        {error && <Banner tone="critical" title="The invite wasn't sent">{error}</Banner>}
        <Field label="Email">
          {(id, d) => <input id={id} aria-describedby={d} type="email" autoComplete="off" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <SelectField label="Role" value={roleId} options={roles.map((r) => ({ value: r.id, label: r.name }))} onChange={setRoleId} help={roles.find((r) => r.id === roleId)?.description} />
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy || !email.trim() || !roleId}>{busy ? "Sending…" : "Send invite"}</button>
        </div>
      </form>
    </Dialog>
  );
}

/* ---------- Roles ---------- */

function Head({ trail, title, badge, actions }: { trail?: { to: string; label: string }[]; title: string; badge?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="settings__headrow">
      <h1 className="settings__title">
        <Icon.shield />
        {(trail ?? []).map((t) => (
          <Fragment key={t.to}>
            <Link to={t.to} className="settings__trail">{t.label}</Link>
            <span className="settings__sep" aria-hidden="true"><Icon.chevR /></span>
          </Fragment>
        ))}
        <span className="settings__titletext">{title}</span>
        {badge}
      </h1>
      {actions && <div className="settings__actions">{actions}</div>}
    </div>
  );
}

function RolesSettings() {
  const part = useLocation().pathname.split("/")[3] ?? "";
  if (part) return <RoleEditor id={part === "new" ? null : part} />;
  return <RolesList />;
}

function RolesList() {
  const nav = useNavigate();
  const { data } = useBusiness();
  const manage = can(data.me.permissions, "business.roles");
  const q = useRoles();
  return (
    <>
      <Head title="Roles" actions={manage ? <Link className="btn btn--primary" to="/settings/roles/new">Add role</Link> : undefined} />
      <Card flush foot="Each solution adds its own permissions when it's switched on.">
        {q.isPending ? (
          <div className="loading">Loading…</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="hide-sm">Type</th>
                <th className="num">People</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.roles ?? []).map((r) => (
                <tr key={r.id} className="is-link" onClick={() => nav(`/settings/roles/${r.id}`)}>
                  <td>
                    <div className="cell__text">
                      <Link to={`/settings/roles/${r.id}`} onClick={(e) => e.stopPropagation()}>
                        <strong>{r.name}</strong>
                      </Link>
                      <span>{r.description}</span>
                    </div>
                  </td>
                  <td className="hide-sm">{r.preset ? <Badge tone="neutral">Preset</Badge> : <Badge tone="info">Custom</Badge>}</td>
                  <td className="num">{r.members ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function TriCheck({ checked, mixed, disabled, onChange, label }: { checked: boolean; mixed: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return <input ref={ref} type="checkbox" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} />;
}

/** "Select all", then each area with how many of its permissions are on, opening to the permissions themselves. */
function PermissionTree({ catalog, value, onChange, readOnly }: { catalog: CatalogGroup[]; value: string[]; onChange?: (v: string[]) => void; readOnly?: boolean }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const all = catalog.flatMap((g) => g.permissions.map((p) => p.key));
  const has = (k: string) => can(value, k);
  const set = (keys: string[], yes: boolean) => {
    const next = new Set(all.filter(has));
    for (const k of keys) {
      if (yes) next.add(k);
      else next.delete(k);
    }
    onChange?.(all.filter((k) => next.has(k)));
  };
  const allOn = all.every(has);
  const someOn = all.some(has);
  const toggle = (g: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(g)) n.delete(g);
      else n.add(g);
      return n;
    });
  return (
    <div className={`perms${readOnly ? " is-readonly" : ""}`}>
      <div className="perms__all">
        {!readOnly && <TriCheck checked={allOn} mixed={someOn && !allOn} onChange={(v) => set(all, v)} label="Select all permissions" />}
        <span>{readOnly ? "What they can do" : "Select all permissions"}</span>
        <button type="button" className="perms__expand" onClick={() => setOpen(open.size === catalog.length ? new Set() : new Set(catalog.map((g) => g.group)))}>
          {open.size === catalog.length ? "Collapse all" : "Expand all"}
        </button>
      </div>
      {catalog.map((g) => {
        const keys = g.permissions.map((p) => p.key);
        const n = keys.filter(has).length;
        const isOpen = open.has(g.group);
        return (
          <div key={g.group} className={`perms__group${isOpen ? " is-open" : ""}`}>
            <div className="perms__row">
              {readOnly ? (
                <span className={`perms__mark${n === keys.length ? " is-all" : n > 0 ? " is-some" : ""}`} aria-hidden="true">{n === keys.length ? <Icon.check /> : null}</span>
              ) : (
                <TriCheck checked={n === keys.length} mixed={n > 0 && n < keys.length} onChange={(v) => set(keys, v)} label={`All of ${g.group}`} />
              )}
              <button type="button" className="perms__name" aria-expanded={isOpen} onClick={() => toggle(g.group)}>
                <span>{g.group}</span>
                <span className="perms__count">{n}/{keys.length}</span>
                <Icon.chevD />
              </button>
            </div>
            {isOpen && (
              <ul className="perms__items">
                {g.permissions.map((p) => (
                  <li key={p.key}>
                    <label className={`check${readOnly ? " is-readonly" : ""}`}>
                      {readOnly ? (
                        <span className={`perms__mark${has(p.key) ? " is-all" : ""}`} aria-hidden="true">{has(p.key) ? <Icon.check /> : null}</span>
                      ) : (
                        <input type="checkbox" checked={has(p.key)} onChange={(e) => set([p.key], e.target.checked)} />
                      )}
                      <span>{p.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RoleEditor({ id }: { id: string | null }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { data } = useBusiness();
  const manage = can(data.me.permissions, "business.roles");
  const q = useRoles();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const role = id ? q.data?.roles.find((r) => r.id === id) : undefined;
  const source = params.get("from") ? q.data?.roles.find((r) => r.id === params.get("from")) : undefined;
  const catalog = q.data?.catalog ?? [];
  const everything = catalog.flatMap((g) => g.permissions.map((p) => p.key));
  const initial = useMemo(
    () =>
      role
        ? { name: role.name, description: role.description, permissions: role.permissions }
        : source
          ? { name: `${source.name} (copy)`, description: source.description, permissions: everything.filter((k) => can(source.permissions, k)) }
          : { name: "", description: "", permissions: [] as string[] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, source, everything.length],
  );
  const locked = !manage || !!role?.preset;
  const ed = useEditor({
    initial,
    revision: 0,
    savedText: id ? "Role saved" : "Role added",
    save: async (d) => {
      const errors: Record<string, string> = {};
      if (!d.name.trim()) errors.name = "Give the role a name.";
      if (d.permissions.length === 0) errors.permissions = "Choose at least one permission.";
      if (Object.keys(errors).length) throw new ApiError(422, "invalid", "Fix the highlighted fields.", errors);
      const body = { name: d.name.trim(), description: d.description.trim(), permissions: d.permissions };
      if (id) await api(`/roles/${id}`, { method: "PATCH", json: body });
      else {
        const r = await api<{ role: Role }>("/roles", { method: "POST", json: body });
        setTimeout(() => nav(`/settings/roles/${r.role.id}`, { replace: true }), 0);
      }
      void qc.invalidateQueries({ queryKey: ["roles"] });
      return { document: { ...d, name: d.name.trim(), description: d.description.trim() }, revision: 0 };
    },
  });
  if (q.isPending) return <div className="loading">Loading…</div>;
  if (id && !role) return <Empty title="That role is not here" text="It may have been removed." />;
  const d = ed.draft;
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/roles/${id}`, { method: "DELETE" });
      void qc.invalidateQueries({ queryKey: ["roles"] });
      toast("Role removed");
      nav("/settings/roles");
    } catch (err) {
      toast(errorText(err), "error");
      setBusy(false);
      setConfirm(false);
    }
  };
  return (
    <>
      {!locked && ed.leaveGuard}
      <Head
        trail={[{ to: "/settings/roles", label: "Roles" }]}
        title={id ? (role?.name ?? "Role") : "Add role"}
        badge={role?.preset ? <Badge tone="neutral">Preset</Badge> : undefined}
        actions={
          id && manage ? (
            <>
              {role?.key !== "owner" && <Link className="btn btn--sm" to={`/settings/roles/new?from=${encodeURIComponent(id)}`}>Duplicate</Link>}
              {!role?.preset && <button type="button" className="btn btn--sm btn--danger-ghost" onClick={() => setConfirm(true)}>Remove role</button>}
            </>
          ) : undefined
        }
      />
      {role?.preset && <Banner tone="info" title="A preset role can't be changed">Duplicate it to make a role of your own from it.</Banner>}
      {ed.notice && <Banner tone={ed.notice.tone} title={ed.notice.title}>{ed.notice.text}</Banner>}
      <Card>
        {locked ? (
          <dl className="dl">
            <dt>Name</dt>
            <dd>{d.name}</dd>
            <dt>What it's for</dt>
            <dd>{d.description || "—"}</dd>
          </dl>
        ) : (
          <>
            <TextField label="Name" value={d.name} onChange={(v) => ed.set("name", v)} maxLength={60} error={ed.errors.name} placeholder="e.g. Check-in volunteer" />
            <TextField label="What it's for" value={d.description} onChange={(v) => ed.set("description", v)} maxLength={240} placeholder="What this role is for" />
          </>
        )}
      </Card>
      <Card title="Permissions" foot={ed.errors.permissions ? <span className="field__error"><Icon.alert />{ed.errors.permissions}</span> : "Ownership stays with the owner, whatever a role holds."}>
        <PermissionTree catalog={catalog} value={d.permissions} onChange={locked ? undefined : (v) => ed.set("permissions", v)} readOnly={locked} />
      </Card>
      <ConfirmDialog
        open={confirm}
        title={`Remove ${role?.name ?? "this role"}?`}
        text={role && (role.members ?? 0) > 0 ? "People still have this role. Give them another role first." : "No one has this role. It's removed for good."}
        confirmLabel="Remove role"
        tone="danger"
        busy={busy}
        onConfirm={() => void remove()}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}

/* ---------- Connectors ---------- */

interface Connector { id: string; capability: string; provider: string; label: string; model: string | null; hint: string | null; shared: boolean; status: string; lastError: string | null; verifiedAt: string | null; lastUsedAt: string | null }
interface Spend { capability: string; source: "own" | "mivio"; calls: number; costUsd: number; costLocal: number }
interface Listing { connectors: Connector[]; ai: { own: boolean; mivio: boolean; ready: boolean }; spend: Spend[]; currency: string; hq: boolean }
interface Model { id: string; name: string; inputPrice: number; outputPrice: number }

function ConnectorsSettings() {
  const { data } = useBusiness();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["connectors"], queryFn: () => api<Listing>("/connectors") });
  const [key, setKey] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<{ recommended: string | null; models: Model[] } | null>(null);
  const [disconnect, setDisconnect] = useState(false);
  const listing = q.data;
  const ai = listing?.connectors.find((c) => c.capability === "ai.text");
  const reload = () => void qc.invalidateQueries({ queryKey: ["connectors"] });
  const money = (n: number) => new Intl.NumberFormat(data.business.locale || "en-AU", { style: "currency", currency: listing?.currency ?? data.business.currency, maximumFractionDigits: n < 1 ? 4 : 2 }).format(n);

  const connect = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/connectors/ai", { method: "POST", json: { provider: "openrouter", key, ...(listing?.hq ? { shared } : {}) } });
      setKey("");
      toast("AI connected");
      reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const patch = async (body: object, done: string) => {
    if (!ai) return;
    try {
      await api(`/connectors/${ai.id}`, { method: "PATCH", json: body });
      toast(done);
      reload();
    } catch (err) {
      toast(errorText(err), "error");
    }
  };
  const total = (listing?.spend ?? []).reduce((n, s) => n + s.costLocal, 0);
  const calls = (listing?.spend ?? []).reduce((n, s) => n + s.calls, 0);
  const chief = data.business.kind === "hq" ? "Alfred" : "your chief of staff";

  if (q.isPending) return <div className="loading">Loading…</div>;
  return (
    <>
      <Card
        title="AI"
        foot={`Powers ${chief} and every AI feature in your solutions. Mivio picks the cheapest model that does the job well; you can choose another.`}
      >
        {error && <Banner tone="critical" title="Not connected">{error}</Banner>}
        {ai ? (
          <ul className="acct-items">
            <li>
              <Icon.sparkle />
              <span>
                <strong>
                  OpenRouter {ai.status === "connected" ? <Badge tone="success">Connected</Badge> : <Badge tone="warn">Needs attention</Badge>} {ai.shared && <Badge tone="info">Offered to every business</Badge>}
                </strong>
                <small>
                  Key {ai.hint} · model {ai.model ?? "not chosen"}
                  {ai.lastError ? ` · last problem: ${ai.lastError}` : ""}
                </small>
              </span>
              <button type="button" className="btn btn--sm" onClick={() => void api<{ recommended: string | null; models: Model[] }>("/connectors/ai/models").then(setModels, (err) => toast(errorText(err), "error"))}>
                Choose model
              </button>
              {listing?.hq && <button type="button" className="btn btn--sm btn--plain" onClick={() => void patch({ shared: !ai.shared }, ai.shared ? "No longer offered" : "Offered to every business")}>{ai.shared ? "Stop offering" : "Offer to every business"}</button>}
              <button type="button" className="btn btn--sm btn--danger-ghost" onClick={() => setDisconnect(true)}>Disconnect</button>
            </li>
          </ul>
        ) : (
          <form onSubmit={(e) => void connect(e)}>
            {listing?.ai.mivio && <Banner tone="info" title="Using Mivio's AI">Connect your own OpenRouter key if you'd rather use your own account.</Banner>}
            <TextField label="OpenRouter key" value={key} onChange={setKey} secret help="Make one at openrouter.ai › Settings › API Keys (it starts with sk-or-). Mivio checks it, then keeps it sealed with this business's own key. It's never shown again." />
            {listing?.hq && <CheckboxField label="Offer this AI to every business on Mivio" checked={shared} onChange={setShared} help="Each business's use is metered to it." />}
            <button type="submit" className="btn btn--primary" disabled={busy || !key.trim()}>{busy ? "Checking…" : "Check and connect"}</button>
          </form>
        )}
        {models && ai && (
          <div className="connector-model">
            <SelectField
              label="Model"
              value={ai.model ?? ""}
              options={models.models.map((m) => ({ value: m.id, label: `${m.name}${m.id === models.recommended ? " (recommended)" : ""} · US$${m.inputPrice.toFixed(2)} in / US$${m.outputPrice.toFixed(2)} out per million tokens` }))}
              onChange={(v) => {
                void patch({ model: v }, "Model changed");
                setModels(null);
              }}
            />
          </div>
        )}
      </Card>
      <Card title="This month">
        <p className="acct-plain">
          {plural(calls, "AI call")} · {money(total)}
        </p>
      </Card>
      <ConfirmDialog
        open={disconnect}
        title="Disconnect AI?"
        text="Features that use it stop until you connect again."
        confirmLabel="Disconnect"
        tone="danger"
        onConfirm={() => {
          setDisconnect(false);
          if (ai) void api(`/connectors/${ai.id}`, { method: "DELETE" }).then(() => { toast("AI disconnected"); reload(); }, (err) => toast(errorText(err), "error"));
        }}
        onCancel={() => setDisconnect(false)}
      />
    </>
  );
}

/* ---------- Files ---------- */

const DOC_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf";
interface FileListing { files: UploadedFile[]; totals: { count: number; bytes: number; limitBytes: number }; canUpload: boolean; canRemove: boolean }

function FilesSettings() {
  const qc = useQueryClient();
  const { day } = useBizDates();
  const q = useQuery({ queryKey: ["files"], queryFn: () => api<FileListing>("/files") });
  const [removing, setRemoving] = useState<UploadedFile | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const reload = () => void qc.invalidateQueries({ queryKey: ["files"] });
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadFile(file);
      toast(`Uploaded ${file.name}`);
      reload();
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const list = q.data;
  const used = list ? Math.min(100, Math.round((list.totals.bytes / list.totals.limitBytes) * 100)) : 0;
  return (
    <>
      <input ref={input} type="file" accept={DOC_ACCEPT} hidden onChange={(e) => void upload(e.target.files?.[0])} />
      <div className="settings__headrow settings__headrow--flush">
        <p className="muted">{list ? `${plural(list.totals.count, "file")} · ${fmtBytes(list.totals.bytes)} of ${fmtBytes(list.totals.limitBytes)} (${used}%)` : " "}</p>
        {list?.canUpload && (
          <div className="settings__actions">
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? "Uploading…" : "Upload a file"}</button>
          </div>
        )}
      </div>
      <Card flush>
        {q.isPending ? (
          <div className="loading">Loading…</div>
        ) : !list || list.files.length === 0 ? (
          <Empty title="Nothing uploaded yet" text="Pictures you add to events, partners or the shop, and documents such as bills and agreements, show up here." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>File</th>
                <th className="hide-sm">Size</th>
                <th className="hide-sm">Added</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.files.map((f) => (
                <tr key={f.id}>
                  <td>
                    <div className="cell">
                      <Thumb src={f.type.startsWith("image/") ? (f.url ?? f.adminPath) : null} icon={<Icon.doc />} />
                      <div className="cell__text">
                        <strong title={f.name}>{f.name}</strong>
                        <span>
                          {f.width ? `${f.width}×${f.height}` : f.type}
                          {f.visibility === "staff" ? " · Staff only" : ""}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="hide-sm">{fmtBytes(f.bytes)}</td>
                  <td className="hide-sm">{f.uploadedBy || "Someone"}, {day(f.createdAt)}</td>
                  <td className="num">
                    <span className="rows__tools">
                      {f.url && <button type="button" className="btn btn--sm btn--plain hide-sm" onClick={() => void navigator.clipboard?.writeText(f.url!).then(() => toast("Link copied"), () => toast("Couldn't copy the link", "error"))}>Copy link</button>}
                      {list.canRemove && <button type="button" className="btn btn--sm btn--danger-ghost" onClick={() => setRemoving(f)}>Remove</button>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <ConfirmDialog
        open={!!removing}
        title={`Remove ${removing?.name ?? ""}?`}
        text="It goes from the members' site straight away. Anything using it shows no picture until you add another."
        confirmLabel="Remove"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          if (!removing) return;
          setBusy(true);
          void api(`/files/${removing.id}`, { method: "DELETE" })
            .then(() => { toast(`Removed ${removing.name}`); reload(); }, (err) => toast(errorText(err), "error"))
            .finally(() => { setBusy(false); setRemoving(null); });
        }}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

/* ---------- Timeline ---------- */

interface Entry { id: number; at: string; actor: { kind: string; name: string }; action: string; summary: string }

function TimelineSettings() {
  const { data } = useBusiness();
  const { when } = useBizDates();
  const q = useQuery({ queryKey: ["timeline"], queryFn: () => api<{ entries: Entry[] }>("/timeline").then((r) => r.entries) });
  return (
    <Card flush foot={`Everything that happens in ${data.business.name}, by people, agents and Mivio itself.`}>
      {q.isPending ? (
        <div className="loading">Loading…</div>
      ) : (q.data ?? []).length === 0 ? (
        <Empty title="Nothing yet" />
      ) : (
        <ul className="rows">
          {q.data!.map((e) => (
            <li key={e.id}>
              <span className="rows__text">
                <strong>{e.summary}</strong>
                <small>
                  {e.actor.name || (e.actor.kind === "system" ? "Mivio" : e.actor.kind)} · {when(e.at)}
                </small>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** For other modules: the businesses list query, re-exported so Settings › General can refresh the menu. */
export { useBusinesses };
